/**
 * FeatureFlagsContext — Phase 0 Safety Infrastructure
 *
 * Fetches /api/config/feature-flags once on app load and caches in React context.
 * All flags default to false (safe) if the fetch fails or a flag is unknown.
 * Components use useFeatureFlags() to get flags with loading/error state.
 *
 * PHI-Zero: no user data is involved in flag fetching.
 * 
 * Phase UX-C: Added explicit loading/error states for deterministic rendering.
 */
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import axios from 'axios';
import { API_BASE_URL } from 'apiConfig';

export interface FeatureFlags {
  // Existing operational flags
  phi_guard_enabled: boolean;
  phi_guard_mode: string;
  require_verified_for_posting: boolean;
  enforce_run_now_quota: boolean;
  messaging_enabled: boolean;
  copilot_enabled: boolean;

  // Phase-0 core flags
  enable_new_landing_page: boolean;
  enable_premium_trials: boolean;
  enable_explore_topic_search_v2: boolean;
  enable_multi_digest_profiles: boolean;
  enable_community_v2: boolean;
  enable_library_audio_digests_v2: boolean;

  // Phase-0 split flags
  enable_multi_digest_profiles_scheduler: boolean;
  enforce_community_digest_membership: boolean;

  // Phase UX-A: App Shell UI Refresh
  enable_app_shell_ui_v2: boolean;

  // Phase SEC-A: Email Verification Requirement
  require_email_verified_for_app_access: boolean;

  // Phase UX-B: Explore Simple PubMed Search
  enable_explore_simple_pubmed_ui: boolean;

  // Phase UX-C: Community Visibility + Subspecialty Limits
  enable_community_visible_only_eligible: boolean;
  enable_community_subspecialty_selection: boolean;

  // Phase UX-D: Full Preferences Wizard per Digest Profile
  enable_digest_profile_full_wizard: boolean;

  // Phase UX-E: Onboarding + Preferences Wizard V2
  enable_onboarding_wizard_v2: boolean;
  enable_preferences_wizard_v2: boolean;
  enable_preferences_dual_write: boolean;

  // Audio + LitScholar Enhancement Flags
  enable_digest_article_audio_links: boolean;
  enable_library_combined_audio_summary: boolean;
  enable_litscholar_v1: boolean;
  enable_litscholar_profile_memory: boolean;

  // Homepage V3 UI Refresh
  enable_home_ui_v3: boolean;

  // NPI Verification
  allow_npi_self_attestation: boolean;

  // Workspace Shell V1
  enable_workspace_shell_v1: boolean;

  // Commercialization: Pricing Page
  enable_pricing_page: boolean;

  // Onboarding: Starter Packs
  enable_starter_packs: boolean;

  // LitHub/LitScreen: Saved Views
  enable_saved_views: boolean;

  // LitHub: Article Notes, Tags, Collections
  enable_article_notes: boolean;

  // LitHub: Reading Goals, Streaks, Progress
  enable_reading_goals: boolean;

  // Settings: Notification Preferences Shell
  enable_notification_prefs: boolean;

  // Navigation V2: Cross-App Context
  enable_navigation_v2: boolean;
}

const SAFE_DEFAULTS: FeatureFlags = {
  phi_guard_enabled: true,
  phi_guard_mode: 'block',
  require_verified_for_posting: false,
  enforce_run_now_quota: false,
  messaging_enabled: false,
  copilot_enabled: false,
  enable_new_landing_page: false,
  enable_premium_trials: false,
  enable_explore_topic_search_v2: false,
  enable_multi_digest_profiles: false,
  enable_community_v2: false,
  enable_library_audio_digests_v2: false,
  enable_multi_digest_profiles_scheduler: false,
  enforce_community_digest_membership: false,
  enable_app_shell_ui_v2: false,
  require_email_verified_for_app_access: false,
  enable_explore_simple_pubmed_ui: false,
  enable_community_visible_only_eligible: false,
  enable_community_subspecialty_selection: false,
  enable_digest_profile_full_wizard: false,
  enable_onboarding_wizard_v2: false,
  enable_preferences_wizard_v2: false,
  enable_preferences_dual_write: false,
  enable_digest_article_audio_links: false,
  enable_library_combined_audio_summary: false,
  enable_litscholar_v1: false,
  enable_litscholar_profile_memory: false,
  enable_home_ui_v3: false,
  allow_npi_self_attestation: false,
  enable_workspace_shell_v1: false,
  enable_pricing_page: false,
  enable_starter_packs: false,
  enable_saved_views: false,
  enable_article_notes: false,
  enable_reading_goals: false,
  enable_notification_prefs: false,
  enable_navigation_v2: false,
};

interface FeatureFlagsContextValue {
  flags: FeatureFlags;
  loading: boolean;
  error: Error | null;
  /** True only after flags have been fetched successfully or failed */
  ready: boolean;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue>({
  flags: SAFE_DEFAULTS,
  loading: true,
  error: null,
  ready: false,
});

export const useFeatureFlags = (): FeatureFlagsContextValue =>
  useContext(FeatureFlagsContext);

/**
 * Hook to get a single feature flag value.
 * Returns false while loading to fail-closed.
 */
export const useFeatureFlag = (flagName: keyof FeatureFlags): boolean => {
  const { flags, ready } = useFeatureFlags();
  // Fail-closed: return false if flags not ready yet
  if (!ready) return false;
  return flags[flagName] === true;
};

interface FeatureFlagsProviderProps {
  children: ReactNode;
}

export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({ children }) => {
  const [state, setState] = useState<{
    flags: FeatureFlags;
    loading: boolean;
    error: Error | null;
    ready: boolean;
  }>({
    flags: SAFE_DEFAULTS,
    loading: true,
    error: null,
    ready: false,
  });

  useEffect(() => {
    let cancelled = false;

    const fetchFlags = async () => {
      try {
        const response = await axios.get<Partial<FeatureFlags>>(
          `${API_BASE_URL}/config/feature-flags`
        );
        if (!cancelled) {
          // Success: merge flags and mark ready in ONE state update
          setState({
            flags: { ...SAFE_DEFAULTS, ...response.data },
            loading: false,
            error: null,
            ready: true,
          });
        }
      } catch (err) {
        if (!cancelled) {
          // Fail safe: use defaults and mark ready in ONE state update
          setState({
            flags: SAFE_DEFAULTS,
            loading: false,
            error: err instanceof Error ? err : new Error('Failed to fetch feature flags'),
            ready: true,
          });
        }
      }
    };

    fetchFlags();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <FeatureFlagsContext.Provider value={state}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};
